import { chakra } from "@chakra-ui/react"

/**
 * The pill every line in an augmentation panel sits in — list items, selectable
 * steps, settings rows. Interactive rows rest on the panel's subtle fill and
 * walk the ramp on hover; static rows carry no fill.
 *
 * Renders a `div` and supports `asChild`, so a row can also *be* a
 * `Checkbox.Root` or a link rather than wrap one, which keeps the whole pill in
 * the control's hit area.
 *
 * Says nothing about the cursor: only the consumer knows whether the whole row
 * is the primary target or a label beside a dedicated drag handle.
 */
export const AugmentationPanelRow = chakra("div", {
  base: {
    alignItems: "center",
    borderRadius: "augmentationPanel",
    color: "augmentationPanel.text",
    display: "flex",
    gap: "augmentationPanel",
    justifyContent: "flex-start",
    minH: "augmentationPanelRow",
    px: "augmentationPanelGutter",
    py: 0,
    // 33px is the pointer row. Touch has no hover, so grow to the native
    // target the theme already names for that case.
    "@media (hover: none)": {
      minH: "augmentationPanelRowTouch"
    },
    transition: "background-color 0.15s ease, border-color 0.15s ease",
    w: "full"
  },
  variants: {
    interactive: {
      true: {
        bg: "augmentationPanel.fill.subtle",
        // Guarded rather than layered under `_disabled`: Chakra emits
        // conditions in its own order, so a plain `_disabled` fill loses to
        // `_hover` and a disabled row lights up under the pointer anyway.
        "&:not(:disabled, [disabled], [data-disabled])": {
          _hover: { bg: "augmentationPanel.fill.muted" },
          _active: { bg: "augmentationPanel.fill.muted" }
        }
      },
      false: {
        bg: "transparent"
      }
    }
  },
  defaultVariants: {
    interactive: true
  }
})
